<script setup lang="ts">
import { RouterLink } from 'vue-router';
import { ChevronDown } from 'lucide-vue-next';
import type { TopMenuLinkView } from '@/nav/menuModel';
import { UserNavIcon } from '@/nav/lucideNavIcons';
import LanguageSelect from '@/components/LanguageSelect.vue';

defineProps<{
  topMenuLinks: TopMenuLinkView[];
  isWideScreenDesktopDrawer: boolean;
  hasTopMenuChildren: (item: TopMenuLinkView) => boolean;
  topMenuChildren: (item: TopMenuLinkView) => Array<{ path: string; label: string }>;
  hasActiveTopMenuChild: (item: TopMenuLinkView) => boolean;
  isTopMenuExpanded: (item: TopMenuLinkView) => boolean;
  isActive: (url: string) => boolean;
  loginLabel: string;
  loginAccountLabel: string;
}>();

defineEmits<{
  'toggle-children': [item: TopMenuLinkView];
  'nav-click': [];
}>();
</script>

<template>
  <div
    class="side-nav__panel side-nav__panel--drawer"
    :class="{ 'side-nav__panel--drawer-desktop': isWideScreenDesktopDrawer }"
  >
    <div class="side-nav__panel-inner">
      <div class="side-nav__scroll">
        <nav class="side-nav__nav" aria-label="Main navigation">
          <template v-for="item in topMenuLinks" :key="item.sectionKey">
            <template v-if="hasTopMenuChildren(item)">
              <div
                class="side-nav__link--parent"
                :class="{
                  'side-nav__link--active': isActive(item.path) || hasActiveTopMenuChild(item),
                  'side-nav__link--expanded': isTopMenuExpanded(item),
                }"
              >
                <RouterLink
                  :to="item.path"
                  class="side-nav__link-anchor"
                  :aria-current="isActive(item.path) ? 'page' : undefined"
                  @click="$emit('nav-click')"
                >
                  <component :is="item.icon" class="side-nav__icon" aria-hidden="true" />
                  <span class="side-nav__label">{{ item.label }}</span>
                </RouterLink>
                <button
                  type="button"
                  class="side-nav__expand-btn"
                  :class="{ 'side-nav__expand-btn--expanded': isTopMenuExpanded(item) }"
                  :aria-expanded="isTopMenuExpanded(item)"
                  :aria-label="`Toggle ${item.label} submenu`"
                  @mousedown.stop.prevent
                  @click.stop.prevent="$emit('toggle-children', item)"
                >
                  <ChevronDown class="side-nav__chevron" aria-hidden="true" />
                </button>
              </div>
              <ul v-if="isTopMenuExpanded(item)" class="side-nav__children">
                <li v-for="child in topMenuChildren(item)" :key="child.path">
                  <RouterLink
                    :to="child.path"
                    class="side-nav__child-link"
                    :class="{ 'side-nav__child-link--active': isActive(child.path) }"
                    :aria-current="isActive(child.path) ? 'page' : undefined"
                    @click="$emit('nav-click')"
                  >
                    {{ child.label }}
                  </RouterLink>
                </li>
              </ul>
            </template>
            <RouterLink
              v-else
              :to="item.path"
              class="side-nav__link"
              :class="{ 'side-nav__link--active': isActive(item.path) }"
              :aria-current="isActive(item.path) ? 'page' : undefined"
              @click="$emit('nav-click')"
            >
              <component :is="item.icon" class="side-nav__icon" aria-hidden="true" />
              <span class="side-nav__label">{{ item.label }}</span>
            </RouterLink>
          </template>
        </nav>

        <hr class="side-nav__divider">

        <div class="side-nav__lang-wrap">
          <LanguageSelect class="side-nav__lang-select" variant="drawer" />
        </div>

        <div class="side-nav__drawer-account">
          <RouterLink
            to="/account"
            class="side-nav__drawer-login"
            @click="$emit('nav-click')"
          >
            <component :is="UserNavIcon" class="side-nav__drawer-login-icon" aria-hidden="true" />
            <span class="side-nav__drawer-login-label side-nav__drawer-login-label--short">{{ loginLabel }}</span>
            <span class="side-nav__drawer-login-label side-nav__drawer-login-label--long">{{ loginAccountLabel }}</span>
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>
